import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Dialog from "components/dialog";
import SelectFormField from "components/select-form-field";
import { FIND_NEXT_AVAILABLE_CHECKOUT_FEATURE } from "constants/feature-flags";
import StartFromPresets, {
  CHECKOUT_START_FROM_PRESETS
} from "features/scheduling/find-next-available/start-from-presets.component";
import { getCheckoutAppointmentTypesParams } from "services/appointment-types";
import { useGetAppointmentTypesQuery } from "services/appointment-types";
import { AppointmentType } from "services/types/appointment-types.type";
import { Patient } from "services/types/patients.type";

interface ScheduleNextAppointmentFormValues {
  appointment_type: number | null;
}

interface ScheduleNextAppointmentModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onScheduleNow: (appointmentTypeId: number) => void;
  readonly onFindNextAvailable?: (
    appointmentType: AppointmentType,
    startFromMonths: number | null
  ) => void;
  readonly patient: Patient | null;
  readonly suggestedAppointmentType: AppointmentType | null;
}

export default function ScheduleNextAppointmentModal({
  open,
  onClose,
  onScheduleNow,
  onFindNextAvailable,
  patient,
  suggestedAppointmentType
}: ScheduleNextAppointmentModalProps) {
  const flags = useFlags();
  const isFindNextCheckoutEnabled =
    Boolean(flags[FIND_NEXT_AVAILABLE_CHECKOUT_FEATURE]) &&
    Boolean(onFindNextAvailable);

  const { data: appointmentTypes = [] } = useGetAppointmentTypesQuery(
    getCheckoutAppointmentTypesParams(isFindNextCheckoutEnabled)
  );

  const appointmentTypeOptions = useMemo(() => {
    return appointmentTypes.map((type) => ({
      value: type.id,
      label: type.name,
      object: type
    }));
  }, [appointmentTypes]);

  const methods = useForm<ScheduleNextAppointmentFormValues>({
    defaultValues: {
      appointment_type: suggestedAppointmentType?.id ?? null
    }
  });

  const { control, watch, reset } = methods;
  const selectedAppointmentTypeId = watch("appointment_type");

  // Reset form when modal opens with a new suggested type
  useEffect(() => {
    if (open && suggestedAppointmentType) {
      reset({
        appointment_type: suggestedAppointmentType.id
      });
    }
  }, [open, suggestedAppointmentType, reset]);

  const selectedAppointmentType = useMemo(() => {
    return appointmentTypes.find(
      (type) => type.id === selectedAppointmentTypeId
    );
  }, [appointmentTypes, selectedAppointmentTypeId]);

  // "When should this happen?" — re-seeded from the selected type's configured
  // interval (Phase C) whenever the type changes; staff can still override.
  const [startFromMonths, setStartFromMonths] = useState<number | null>(null);
  useEffect(() => {
    if (!open) return;
    setStartFromMonths(
      selectedAppointmentType?.next_appointment_interval_months ?? null
    );
  }, [
    open,
    selectedAppointmentType?.id,
    selectedAppointmentType?.next_appointment_interval_months
  ]);

  const handleScheduleNow = useCallback(() => {
    if (selectedAppointmentTypeId) {
      onScheduleNow(selectedAppointmentTypeId);
    }
  }, [selectedAppointmentTypeId, onScheduleNow]);

  const handleFindNextAvailable = useCallback(() => {
    if (selectedAppointmentType && onFindNextAvailable) {
      onFindNextAvailable(selectedAppointmentType, startFromMonths);
    }
  }, [selectedAppointmentType, onFindNextAvailable, startFromMonths]);

  const handleClose = useCallback(
    (_event: object, _reason: string) => {
      onClose();
    },
    [onClose]
  );

  const patientFullName = patient
    ? `${patient.first_name} ${patient.last_name}`
    : "";

  return (
    <FormProvider {...methods}>
      <Dialog
        title="Schedule next appointment?"
        open={open}
        onClose={handleClose}
        minWidth={500}
        maxWidth="sm"
        dialogContentProps={{
          sx: {
            padding: "24px !important"
          }
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Patient
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {patientFullName}
            </Typography>
          </Box>

          <Box>
            <SelectFormField
              options={appointmentTypeOptions}
              name="appointment_type"
              label="Appointment type"
              control={control}
              required
            />
            {selectedAppointmentType && (
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "2px",
                    backgroundColor: selectedAppointmentType.color,
                    flexShrink: 0
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Duration: {selectedAppointmentType.default_duration}
                </Typography>
              </Stack>
            )}
          </Box>

          {isFindNextCheckoutEnabled && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                When should this happen?
              </Typography>
              <StartFromPresets
                value={startFromMonths}
                onChange={(value) =>
                  setStartFromMonths(value === "custom" ? null : value)
                }
                options={CHECKOUT_START_FROM_PRESETS}
              />
            </Box>
          )}
        </Stack>

        <DialogActions sx={{ px: 0, pt: 3, pb: 0 }}>
          <Button
            variant={isFindNextCheckoutEnabled ? "text" : "outlined"}
            onClick={onClose}
          >
            Not now
          </Button>
          {isFindNextCheckoutEnabled ? (
            <>
              <Button
                variant="outlined"
                onClick={handleScheduleNow}
                disabled={!selectedAppointmentTypeId}
              >
                Pick on calendar
              </Button>
              <Button
                variant="contained"
                onClick={handleFindNextAvailable}
                disabled={!selectedAppointmentType}
                data-testid="schedule-next-find-available"
              >
                Find next available
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleScheduleNow}
              disabled={!selectedAppointmentTypeId}
            >
              Schedule now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
